"use client";

import { useId, useRef, useSyncExternalStore, type KeyboardEvent } from "react";
import type { DocsSnippets } from "../types";
import { CodePre } from "./CodeBlock";
import { CopyButton } from "./CopyButton";
import type { CodeLanguage } from "./highlight";

type Lang = keyof DocsSnippets;

const TABS: { id: Lang; label: string; language: CodeLanguage }[] = [
  { id: "curl", label: "cURL", language: "shell" },
  { id: "javascript", label: "JavaScript", language: "javascript" },
  { id: "python", label: "Python", language: "python" },
];

// One choice for every sample on the page, remembered per browser: picking
// Python once shouldn't mean picking it again on the next endpoint.
const STORAGE_KEY = "steampeek:api-docs:lang";
const listeners = new Set<() => void>();
let chosen: Lang | null = null;

function isLang(v: unknown): v is Lang {
  return v === "curl" || v === "javascript" || v === "python";
}

function getSnapshot(): Lang {
  if (chosen) return chosen;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLang(stored) ? stored : "curl";
  } catch {
    return "curl";
  }
}

function getServerSnapshot(): Lang {
  return "curl";
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function choose(lang: Lang): void {
  chosen = lang;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Storage blocked: the choice still holds for this tab.
  }
  for (const l of listeners) l();
}

/** curl / JavaScript / Python versions of one example call. */
export function CodeTabs({ snippets }: { snippets: DocsSnippets }) {
  const active = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const baseId = useId();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = TABS.find((t) => t.id === active) ?? TABS[0];

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const index = TABS.findIndex((t) => t.id === current.id);
    const next = (index + (e.key === "ArrowRight" ? 1 : TABS.length - 1)) % TABS.length;
    choose(TABS[next].id);
    tabRefs.current[next]?.focus();
  }

  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-[#2a475e]/70 bg-[#0e1620]">
      <div className="flex items-center justify-between gap-2 border-b border-[#2a475e]/60 bg-[#111a25] pr-1.5">
        <div role="tablist" aria-label="Language" className="flex min-w-0 overflow-x-auto" onKeyDown={onKeyDown}>
          {TABS.map((tab, i) => {
            const selected = tab.id === current.id;
            return (
              <button
                key={tab.id}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`${baseId}-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={`${baseId}-panel`}
                tabIndex={selected ? 0 : -1}
                onClick={() => choose(tab.id)}
                className={`relative shrink-0 px-3 py-2 text-xs font-medium transition-colors ${
                  selected ? "text-white" : "text-[#8f98a0] hover:text-[#c7d5e0]"
                }`}
              >
                {tab.label}
                {selected && <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#66c0f4]" aria-hidden />}
              </button>
            );
          })}
        </div>
        <CopyButton text={snippets[current.id]} />
      </div>
      <div role="tabpanel" id={`${baseId}-panel`} aria-labelledby={`${baseId}-tab-${current.id}`}>
        <CodePre code={snippets[current.id]} language={current.language} />
      </div>
    </div>
  );
}
